/**
 * SearchFilterBar — pill-shaped search input + optional status selector.
 *
 * Unified card container wrapping a transparent search field (with icon),
 * a hairline divider, and an embedded combobox trigger. The outer pill provides
 * the border and shadow — inner elements have no independent styling.
 *
 * The filter section (divider + trigger + panel) only renders when `options`
 * contains at least one entry. Pass no `options` for search-only mode.
 *
 * Both inputs are fully controlled — the caller owns state.
 *
 * @layer-1 — reusable across admin and publisher title tabs
 */
import { useMemo } from "react";
import { Search, ChevronDown } from "lucide-react";
import { Combobox as ComboboxPrimitive } from "@base-ui/react";
import {
  Combobox,
  ComboboxList,
  ComboboxEmpty,
} from "@/components/ui/combobox";
import { cn } from "@/shared/lib/utils";
import { getEffectiveDir } from "@/shared/lib/direction";
import { useTranslation } from "react-i18next";

export interface SearchFilterOption {
  value: string;
  label: string;
}

interface SearchFilterBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  /** Current filter value. Required when `options` is provided. */
  filter?: string;
  /** Called when the user picks a filter option. Required when `options` is provided. */
  onFilterChange?: (value: string) => void;
  /** Filter options. When empty or omitted the filter section is hidden. */
  options?: SearchFilterOption[];
  searchPlaceholder?: string;
  /** Label for the "show all" option (value = ""). Defaults to common.all. */
  allLabel?: string;
  className?: string;
}

export function SearchFilterBar({
  search,
  onSearchChange,
  filter = "",
  onFilterChange,
  options = [],
  searchPlaceholder,
  allLabel,
  className,
}: SearchFilterBarProps) {
  const { t } = useTranslation("common");

  const showFilter = options.length > 0;

  const allOption: SearchFilterOption = useMemo(
    () => ({ value: "", label: allLabel ?? t("common.all") }),
    [allLabel, t],
  );

  const allOptions = useMemo(
    () => [allOption, ...options],
    [allOption, options],
  );

  const selectedOption = useMemo(
    () => allOptions.find((o) => o.value === filter) ?? allOption,
    [allOptions, filter, allOption],
  );

  /** Longest label — drives the fixed width of the trigger via CSS grid overlay. */
  const longestLabel = useMemo(
    () => allOptions.reduce((a, b) => (a.label.length >= b.label.length ? a : b)).label,
    [allOptions],
  );

  return (
    <div
      className={cn("flex items-center gap-3 p-1.5 rounded-[16px]", className)}
      style={{
        background: "var(--card)",
        border: "1px solid var(--border-light)",
        boxShadow: "0 2px 20px rgba(26,25,23,0.06)",
      }}
    >
      {/* Search input */}
      <div className="relative flex-1 min-w-0">
        <span
          className="absolute start-4 top-1/2 -translate-y-1/2 pointer-events-none"
          style={{ color: "var(--text-tertiary)" }}
          aria-hidden
        >
          <Search size={15} strokeWidth={2} />
        </span>
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchPlaceholder ?? t("common.search")}
          dir={getEffectiveDir()}
          className={cn(
            "w-full py-2.5 ps-10 pe-4 text-[13px]",
            "bg-transparent outline-none",
            "text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]",
            "rounded-[14px] transition-colors duration-150",
            "focus:outline-none",
          )}
        />
      </div>

      {/* Divider + filter combobox — only when options are provided */}
      {showFilter && (
        <>
          {/* Divider — hidden on mobile */}
          <div
            className="hidden md:block w-px self-stretch shrink-0"
            style={{ background: "var(--border-light)" }}
            aria-hidden
          />

          {/* Filter combobox — embedded trigger, hidden on mobile */}
          <Combobox
            value={selectedOption}
            onValueChange={(opt: SearchFilterOption | null) =>
              onFilterChange?.(opt?.value ?? "")
            }
            itemToStringLabel={(opt) => opt?.label ?? ""}
            itemToStringValue={(opt) => opt?.value ?? ""}
          >
            {/* Trigger: inline text+chevron, no independent border.
                Label uses inline-grid overlay so trigger width is fixed to the longest option. */}
            <ComboboxPrimitive.Trigger
              className="hidden md:flex items-center gap-1.5 px-3 py-2 cursor-pointer text-[13px] focus:outline-none"
              style={{ color: "var(--text-primary)" }}
            >
              <span className="inline-grid grid-cols-1 grid-rows-1">
                {/* Invisible sizer — forces column to longest-option width */}
                <span
                  aria-hidden
                  className="row-start-1 col-start-1 invisible pointer-events-none whitespace-nowrap"
                >
                  {longestLabel}
                </span>
                {/* Visible selected label — overlaid in same grid cell */}
                <span className="row-start-1 col-start-1 whitespace-nowrap">
                  {selectedOption.label}
                </span>
              </span>
              <ChevronDown size={12} style={{ color: "var(--text-tertiary)" }} />
            </ComboboxPrimitive.Trigger>

            {/* Panel — same CSS vars as ThemedCombobox */}
            <ComboboxPrimitive.Portal>
              <ComboboxPrimitive.Positioner
                side="bottom"
                sideOffset={6}
                align="end"
                className="isolate z-50"
              >
                <ComboboxPrimitive.Popup
                  data-slot="combobox-content"
                  className="group/combobox-content relative max-h-(--available-height) w-(--anchor-width) min-w-[160px] max-w-(--available-width) origin-(--transform-origin) overflow-hidden rounded-[var(--radius-input)] duration-100 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95"
                  style={{
                    background: "var(--card)",
                    border: "1.5px solid var(--input-border)",
                  }}
                >
                  <ComboboxEmpty
                    className="hidden w-full justify-center py-2 text-center text-sm group-data-empty/combobox-content:flex"
                    style={{ color: "var(--text-tertiary)" }}
                  >
                    {t("common.noResults")}
                  </ComboboxEmpty>
                  <ComboboxList className="p-1">
                    {allOptions.map((opt) => (
                      <ComboboxPrimitive.Item
                        key={opt.value}
                        value={opt}
                        className="relative flex w-full min-h-10 cursor-default items-center ps-8 pe-4 py-2 text-sm outline-hidden select-none data-highlighted:bg-[var(--card-hover)] data-highlighted:text-[var(--foreground)] rounded-[8px]"
                      >
                        <span
                          className="text-[13px]"
                          style={{ color: "var(--foreground)" }}
                        >
                          {opt.label}
                        </span>
                        <ComboboxPrimitive.ItemIndicator
                          render={
                            <span className="pointer-events-none absolute start-2 flex size-4 items-center justify-center" />
                          }
                        >
                          <span
                            className="text-xs"
                            style={{ color: "var(--accent-gold, #d4a547)" }}
                          >
                            ✓
                          </span>
                        </ComboboxPrimitive.ItemIndicator>
                      </ComboboxPrimitive.Item>
                    ))}
                  </ComboboxList>
                </ComboboxPrimitive.Popup>
              </ComboboxPrimitive.Positioner>
            </ComboboxPrimitive.Portal>
          </Combobox>
        </>
      )}
    </div>
  );
}
